import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { HandHelping, HandHeart, ShoppingBasket, CarFront, HeartHandshake, MapPin, ArrowLeft, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CATS = [
  { key: "groceries", label: "Groceries & Errands", icon: ShoppingBasket },
  { key: "transport", label: "Transport & Mobility", icon: CarFront },
  { key: "companionship", label: "Companionship", icon: HeartHandshake },
];

export default function CreatePost() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [kind, setKind] = useState("request");
  const [categories, setCategories] = useState([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [address, setAddress] = useState(user?.address || "");
  const [coords, setCoords] = useState({ lat: user?.lat ?? null, lng: user?.lng ?? null });
  const [locating, setLocating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [searchParams] = useSearchParams();
  const onBehalf = searchParams.get("for");
  const [behalfName, setBehalfName] = useState("");

  useEffect(() => {
    if (onBehalf) api.get("/family/members").then((r) => {
      const m = r.data.find((x) => x.user_id === onBehalf);
      if (m) { setBehalfName(m.name); setAddress(m.address || ""); setCoords({ lat: m.lat ?? null, lng: m.lng ?? null }); }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onBehalf]);

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Location not supported on this device");
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setLocating(false); toast.success("Location added"); },
      () => { setLocating(false); toast.error("Couldn't get your location. You can type an address instead."); }
    );
  };

  const toggleCat = (key) =>
    setCategories((prev) => (prev.includes(key) ? prev.filter((c) => c !== key) : [...prev, key]));

  const submit = async (e) => {
    e.preventDefault();
    if (categories.length === 0) return toast.error("Please choose at least one type of help");
    setBusy(true);
    try {
      const { data } = await api.post("/posts", { kind, categories, title, description, address, lat: coords.lat, lng: coords.lng, on_behalf_of: onBehalf || undefined });
      toast.success("Your post is live!");
      navigate(`/app/post/${data.post_id}`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-forest-700 font-semibold mb-4" data-testid="back-btn">
        <ArrowLeft size={20} strokeWidth={2.5} /> Back
      </button>
      <h1 className="font-head text-3xl sm:text-4xl font-bold text-forest-900 mb-6">Create a post</h1>
      {onBehalf && (
        <div data-testid="behalf-banner" className="bg-gold-light border-2 border-gold rounded-2xl p-4 mb-5 font-semibold text-forest-900">
          You're posting on behalf of {behalfName || "your family member"}.
        </div>
      )}
      <form onSubmit={submit} className="space-y-6">
        <div className="bg-white rounded-2xl border-2 border-border shadow-sm p-6">
          <label className="font-bold text-forest-900 text-lg mb-3 block">Are you asking or offering?</label>
          <div className="grid grid-cols-2 gap-3">
            <button type="button" onClick={() => setKind("request")} data-testid="kind-request"
              className={`min-h-[64px] rounded-xl border-2 font-bold text-lg flex items-center justify-center gap-2 transition-colors ${kind === "request" ? "bg-terracotta text-white border-terracotta" : "bg-white text-forest-700 border-border"}`}>
              <HandHelping size={24} strokeWidth={2.5} /> I need help
            </button>
            <button type="button" onClick={() => setKind("offer")} data-testid="kind-offer"
              className={`min-h-[64px] rounded-xl border-2 font-bold text-lg flex items-center justify-center gap-2 transition-colors ${kind === "offer" ? "bg-forest-700 text-white border-forest-700" : "bg-white text-forest-700 border-border"}`}>
              <HandHeart size={24} strokeWidth={2.5} /> I'm offering
            </button>
          </div>
        </div>

        <div className="bg-white rounded-2xl border-2 border-border shadow-sm p-6">
          <label className="font-bold text-forest-900 text-lg mb-1 block">What kind of help?</label>
          <p className="text-forest-500 mb-3">Choose one or more that apply.</p>
          <div className="grid sm:grid-cols-3 gap-3">
            {CATS.map((c) => {
              const on = categories.includes(c.key);
              return (
                <button key={c.key} type="button" onClick={() => toggleCat(c.key)} data-testid={`cat-${c.key}`}
                  className={`relative min-h-[96px] rounded-xl border-2 font-bold flex flex-col items-center justify-center gap-2 px-2 text-center transition-colors ${on ? "bg-gold-light border-gold text-forest-900" : "bg-white text-forest-700 border-border hover:border-forest-500"}`}>
                  {on && <span className="absolute top-2 right-2 w-6 h-6 rounded-full bg-terracotta text-white flex items-center justify-center"><Check size={16} strokeWidth={3} /></span>}
                  <c.icon size={30} strokeWidth={2.5} className={on ? "text-terracotta" : "text-forest-700"} />
                  <span className="text-sm leading-tight">{c.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="bg-white rounded-2xl border-2 border-border shadow-sm p-6 space-y-4">
          <div>
            <label className="font-bold text-forest-900 text-lg mb-1.5 block">Title</label>
            <input data-testid="post-title" required value={title} onChange={(e) => setTitle(e.target.value)} maxLength={80}
              placeholder={kind === "request" ? "e.g. Need a ride to the clinic Tuesday" : "e.g. Happy to help with weekly groceries"}
              className="w-full min-h-[56px] text-lg p-4 rounded-xl border-2 border-border focus:border-terracotta focus:ring-4 focus:ring-terracotta/20 outline-none bg-cream" />
          </div>
          <div>
            <label className="font-bold text-forest-900 text-lg mb-1.5 block">Details</label>
            <textarea data-testid="post-description" required value={description} onChange={(e) => setDescription(e.target.value)} rows={4}
              placeholder="Share a little more so people know how to help."
              className="w-full text-lg p-4 rounded-xl border-2 border-border focus:border-terracotta focus:ring-4 focus:ring-terracotta/20 outline-none bg-cream resize-none" />
          </div>
          <div>
            <label className="font-bold text-forest-900 text-lg mb-1.5 block">Area / neighbourhood</label>
            <input data-testid="post-address" value={address} onChange={(e) => setAddress(e.target.value)}
              placeholder="e.g. Downtown, Riverside"
              className="w-full min-h-[56px] text-lg p-4 rounded-xl border-2 border-border focus:border-terracotta focus:ring-4 focus:ring-terracotta/20 outline-none bg-cream" />
            <button type="button" onClick={useMyLocation} data-testid="use-location-btn"
              className={`mt-3 min-h-[52px] px-5 rounded-full font-semibold border-2 flex items-center gap-2 transition-colors ${coords.lat != null ? "bg-forest-700 text-white border-forest-700" : "bg-white text-forest-700 border-border hover:border-forest-500"}`}>
              {locating ? <Loader2 size={20} className="animate-spin" /> : <MapPin size={20} strokeWidth={2.5} />}
              {coords.lat != null ? "Location added ✓" : "Use my location for distance matching"}
            </button>
          </div>
        </div>

        <button type="submit" disabled={busy} data-testid="post-submit"
          className="w-full min-h-[60px] rounded-full bg-terracotta text-white font-bold text-lg hover:bg-terracotta-dark transition-transform active:scale-95 disabled:opacity-60">
          {busy ? "Posting…" : "Post to the community"}
        </button>
      </form>
    </div>
  );
}
